import { useEffect, useState } from "react";
import { Code2, Eye, EyeOff, Home, Plus, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { useLets } from "@/hooks/useLets";
import { useAgent } from "@/hooks/useAgent";
import { can } from "@/lib/roles";
import { applyUrl, maskKey, shortDate } from "@/lib/format";
import { AgentLayout } from "@/components/agent/AgentLayout";
import { PropertyImage } from "@/components/agent/PropertyImage";
import { SettingsCard } from "@/components/ui/settings-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CopyButton, EmptyState, ListingTag, Loading, PageHeader, PrimaryButton, SecondaryButton } from "@/components/agent/ui";

const KeyBox = ({ children, testid }) => (
  <code className="min-w-0 flex-1 truncate rounded-md border bg-panel px-2.5 py-1.5 font-mono text-xs" data-testid={testid}>{children}</code>
);

export const CodeBlock = ({ file, code, copy, testid }) => (
  <div className="overflow-hidden rounded-lg border" data-testid={testid}>
    <div className="flex h-9 items-center justify-between border-b bg-panel pl-3 pr-1">
      <span className="font-mono text-xs text-muted-foreground">{file}</span>
      <CopyButton value={copy ?? code} label={`${file} copied`} testid={`${testid}-copy`} />
    </div>
    <pre className="overflow-x-auto bg-background p-3 font-mono text-xs leading-relaxed">{code}</pre>
  </div>
);

const KeyRow = ({ label, detail, children }) => (
  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
    <div className="sm:w-44">
      <div className="flex items-center gap-2 text-sm font-medium">{label}</div>
      <p className="text-xs text-muted-foreground">{detail}</p>
    </div>
    <div className="flex min-w-0 flex-1 items-center gap-1">{children}</div>
  </div>
);

const RollDialog = ({ open, onClose, onDone }) => {
  const [busy, setBusy] = useState(false);
  const roll = async () => {
    setBusy(true);
    try {
      onDone((await api.post("/agent/api-key/roll")).data);
      toast.success("New secret key created");
      onClose();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="roll-dialog">
        <DialogHeader>
          <DialogTitle>Roll secret key?</DialogTitle>
          <DialogDescription>The current key stops working straight away. Put the new one in your server's .env before you go back to your site.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <SecondaryButton size="default" onClick={onClose} testid="roll-cancel">Cancel</SecondaryButton>
          <Button variant="destructive" isLoading={busy} onClick={roll} data-testid="roll-confirm">Roll key</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EmbedDialog = ({ property, onClose }) => {
  const link = property ? applyUrl(property.link_token) : "";
  return (
    <Dialog open={!!property} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl" data-testid="embed-dialog">
        <DialogHeader>
          <DialogTitle>Add to your listing</DialogTitle>
          <DialogDescription>{property?.address}. Anyone who follows this link starts their own application for this property.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <span className="text-sm font-medium">Apply link</span>
          <div className="flex items-center gap-1"><KeyBox testid="embed-apply-link">{link}</KeyBox><CopyButton value={link} label="Apply link copied" testid="embed-apply-link-copy" /></div>
        </div>
        <div className="grid gap-2">
          <span className="text-sm font-medium">Button for your website</span>
          <CodeBlock file="listing.html" code={`<a href="${link}">Apply with Landin</a>`} testid="embed-snippet" />
        </div>
        <DialogFooter><PrimaryButton size="default" onClick={onClose} testid="embed-done">Done</PrimaryButton></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ApiKeys = ({ keys, setKeys, error }) => {
  const [shown, setShown] = useState(false);
  const [rolling, setRolling] = useState(false);
  if (error) return <SettingsCard title="API keys" id="api-keys" testid="developers-panel"><p className="text-sm text-muted-foreground" data-testid="api-keys-error">{error}</p></SettingsCard>;
  if (!keys) return <SettingsCard title="API keys" id="api-keys" testid="developers-panel"><Loading /></SettingsCard>;
  const secret = shown ? keys.api_key : maskKey(keys.api_key);
  return (
    <>
      <SettingsCard title="API keys" description="Your account keys. Use them to create properties from your own site or listing tool." divide id="api-keys" testid="developers-panel">
        <KeyRow label="Publishable key" detail="Safe in your website's code">
          <KeyBox testid="publishable-key">{keys.publishable_key}</KeyBox>
          <CopyButton value={keys.publishable_key} label="Publishable key copied" testid="publishable-key-copy" />
          <span className="size-7 shrink-0" />
        </KeyRow>
        <KeyRow label={<>Secret key <Badge variant="warning">Server only</Badge></>} detail={keys.created_at ? `Created ${shortDate(keys.created_at)}` : ""}>
          <KeyBox testid="secret-key">{secret}</KeyBox>
          <CopyButton value={keys.api_key} label="Secret key copied" testid="secret-key-copy" />
          <Button variant="ghost" size="icon-sm" className="text-muted-foreground" onClick={() => setShown(!shown)}
            aria-label={shown ? "Hide secret key" : "Reveal secret key"} data-testid="secret-key-reveal">{shown ? <EyeOff /> : <Eye />}</Button>
        </KeyRow>
        <div className="flex justify-end">
          <Button variant="ghost-muted" size="sm" onClick={() => setRolling(true)} data-testid="roll-button"><RotateCw /> Roll secret key</Button>
        </div>
      </SettingsCard>
      <SettingsCard title="Add to your project" description="Paste into the .env file on your server. Keep that file out of version control." flush id="env" testid="env-snippet">
        <div className="p-3">
          <CodeBlock file=".env" testid="env-code"
            code={`LANDIN_PUBLISHABLE_KEY=${keys.publishable_key}\nLANDIN_SECRET_KEY=${secret}`}
            copy={`LANDIN_PUBLISHABLE_KEY=${keys.publishable_key}\nLANDIN_SECRET_KEY=${keys.api_key}`} />
        </div>
      </SettingsCard>
      <RollDialog open={rolling} onClose={() => setRolling(false)} onDone={setKeys} />
    </>
  );
};

const PropertyKeyRow = ({ l, i, onEmbed }) => (
  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4" data-testid={`property-key-row-${i}`}>
    <div className="flex items-center gap-3 sm:w-72">
      <span className="h-9 w-12 shrink-0 overflow-hidden rounded-md"><PropertyImage src={l.image_data} /></span>
      <div className="min-w-0">
        <p className="text-sm font-medium leading-snug">{l.address}</p>
        <ListingTag status={l.listing_status} />
      </div>
    </div>
    <div className="flex min-w-0 flex-1 items-center gap-1">
      <KeyBox testid={`property-key-${i}`}>{l.link_token}</KeyBox>
      <CopyButton value={l.link_token} label="Property key copied" testid={`property-key-copy-${i}`} />
      <Button variant="outline" size="sm" disabled={l.listing_status === "rented"} onClick={onEmbed} data-testid={`property-embed-${i}`}><Code2 /> Embed</Button>
    </div>
  </div>
);

export default function Developers() {
  const agent = useAgent();
  const { lets } = useLets();
  const [keys, setKeys] = useState(null);
  const [keyError, setKeyError] = useState("");
  const [embed, setEmbed] = useState(null);
  const allowed = agent && can(agent.role, "keys");

  useEffect(() => {
    if (!agent) return;
    if (!allowed) {
      setKeyError("Only owners and admins can see the account keys.");
      return;
    }
    api.get("/agent/api-key").then((r) => setKeys(r.data)).catch((err) => setKeyError(errorText(err)));
  }, [agent, allowed]);

  return (
    <AgentLayout>
      <PageHeader title="Developers" description="Connect Landin to your website: one set of account keys, plus a key for each property." />
      <ApiKeys keys={keys} setKeys={setKeys} error={keyError} />
      <SettingsCard title="Property keys" description="One per property. It opens that property's application, so each listing on your site links to the right one."
        action={can(agent?.role, "edit") && <SecondaryButton to="/lets/new" testid="developers-add-property"><Plus /> Add property</SecondaryButton>}
        divide={!!lets?.length} id="property-keys" testid="property-keys">
        {!lets ? <Loading /> : lets.length
          ? lets.map((l, i) => <PropertyKeyRow key={l.id} l={l} i={i} onEmbed={() => setEmbed(l)} />)
          : <EmptyState icon={Home} title="No properties yet" text="Add a property to get its key." testid="property-keys-empty" />}
      </SettingsCard>
      <EmbedDialog property={embed} onClose={() => setEmbed(null)} />
    </AgentLayout>
  );
}
